/*
 * @Copyright 2024-2025. Institute for Future Intelligence, Inc.
 */

import { useEffect, useMemo, useRef, useState } from 'react';
import dayjs from 'dayjs';
import { deleteDoc, deleteField, doc, getDoc, setDoc, Timestamp, writeBatch } from 'firebase/firestore';
import { useTranslation } from 'react-i18next';
import { useStore } from './stores/common';
import { clearUndoHistory } from './undo/setWithUndo';
import { usePrimitiveStore } from './stores/commonPrimitive';
import { useDataStore } from './stores/commonData.ts';
import { useRefStore } from './stores/commonRef.ts';
import * as Selector from './stores/selector';
import { auth, firestore } from './firebase.ts';
import { captureThumbnail, setMessage } from './helpers';
import {
  fetchProject,
  postFetch,
  projectIndexSetPayload,
  publicProjectDocId,
  publishProject as publishProjectRemote,
  readProjectIndex,
  readTrashIndex,
  reconcileProjectIndex,
  removeProjectIndexEntry,
  setProjectIndexEntry,
  stripProjectStateForSave,
  syncMoleculesFromRef,
  syncPublicProjectMeta,
  unpublishProject as unpublishProjectRemote,
  uploadBioThumbnails,
  reconcileBioThumbnails,
  removeTrashIndexEntry,
  deleteThumbnail,
} from './cloudProjectUtil';
import { cancelCurrentGeneration } from './generationAbort';
import {
  captureCrystalDynamicsSnapshot,
  crystalDynamicsSnapshotSource,
} from './models/crystal/buildCrystalDynamics.ts';
import { ProjectUtil } from './project/ProjectUtil.ts';
import { buildChatField, CHAT_GZ_FIELD, SAFE_DOC_BYTES } from './project/chatCompression.ts';
import { buildBiomoleculeField, BIO_GZ_FIELD } from './biomolecule/biomoleculeCompression.ts';
import { ChatMessageInterface, ProjectInfo, ProjectState, RecentProjectInfo } from './types';
import {
  DEFAULT_CAMERA_POSITION,
  DEFAULT_CAMERA_ROTATION,
  DEFAULT_CAMERA_UP,
  DEFAULT_PAN_CENTER,
  isBiomoleculeProject,
  ProjectType,
  TRASH_RETENTION_DAYS,
} from './constants';

// Cap for the File-menu "Recent Projects" list.
const MAX_RECENT_PROJECTS = 10;

export interface CloudProjects {
  myProjectsRef: React.MutableRefObject<ProjectInfo[]>;
  myTrashRef: React.MutableRefObject<ProjectInfo[]>;
  setProjectState: (ps: ProjectState | null) => void;
  loadProject: (uid: string, title: string) => Promise<void>;
  createNewProject: () => Promise<void>;
  saveProject: () => Promise<void>;
  saveProjectAs: () => Promise<void>;
  showMyProjectsList: () => void;
  deleteProject: (title: string) => Promise<void>;
  restoreProject: (title: string) => Promise<void>;
  deleteProjectForever: (title: string) => Promise<void>;
  emptyTrash: () => Promise<void>;
  fetchMyTrash: () => Promise<ProjectInfo[]>;
  renameProject: (oldTitle: string, newTitle: string) => Promise<void>;
  publishProject: (title: string) => Promise<boolean>;
  unpublishProject: (title: string) => Promise<boolean>;
  updateProjects: () => void;
}

export const useCloudProjects = (): CloudProjects => {
  const setCommonStore = useStore(Selector.set);
  const language = useStore(Selector.language);
  const user = useStore(Selector.user);
  const setWaiting = usePrimitiveStore(Selector.setWaiting);
  const showProjectListPanel = usePrimitiveStore(Selector.showProjectListPanel);
  const saveAndThenOpenProjectFlag = usePrimitiveStore(Selector.saveAndThenOpenProjectFlag);
  const setChanged = usePrimitiveStore(Selector.setChanged);
  const moleculesRef = useRefStore.getState().moleculesRef;
  const energyTimeSeries = useDataStore(Selector.energyTimeSeries);
  const speedArrayMap = useDataStore(Selector.speedArrayMap);
  const positionTimeSeriesMap = useDataStore(Selector.positionTimeSeriesMap);

  const [updateFlag, setUpdateFlag] = useState(false);
  const [updateMyProjectsFlag, setUpdateMyProjectsFlag] = useState(false);
  const myProjectsRef = useRef<ProjectInfo[]>([]);
  // Soft-deleted (trashed) projects, read from the user doc's trashIndex map.
  // Lazily filled by fetchMyTrash when the Trash view is opened.
  const myTrashRef = useRef<ProjectInfo[]>([]);
  // Bumped on every loadProject call. The Firebase SDK doesn't expose a way
  // to cancel an in-flight getDoc, so the resolved callback compares its
  // captured token against this and discards stale results.
  const loadProjectTokenRef = useRef(0);

  const { t } = useTranslation();
  const lang = useMemo(() => ({ lng: language }), [language]);

  useEffect(() => {
    if (myProjectsRef.current) {
      myProjectsRef.current.sort((a, b) => b.timestamp - a.timestamp);
    }
  }, [updateMyProjectsFlag]);

  // Push a project to the front of the per-browser "Recent Projects" list
  // (deduped by owner+title, capped). Called after a successful open or save.
  const recordRecentProject = (owner: string, title: string, type: ProjectType, timestamp: number) => {
    if (!owner || !title) return;
    // Recent Projects is a shortcut to the USER'S OWN projects only. Opening
    // someone else's project via a shared link (owner ≠ the signed-in user)
    // must not add it — it isn't editable and isn't theirs to "reopen". Save /
    // Save As always target the user's own collection, so those still record.
    const me = useStore.getState().user.uid;
    if (!me || owner !== me) return;
    setCommonStore((state) => {
      const rest = state.recentlyOpenedProjects.filter((p) => !(p.owner === owner && p.title === title));
      const entry: RecentProjectInfo = { owner, title, type, timestamp };
      state.recentlyOpenedProjects = [entry, ...rest].slice(0, MAX_RECENT_PROJECTS);
    });
  };

  const resetProject = () => {
    energyTimeSeries.clear();
    speedArrayMap.clear();
    positionTimeSeriesMap.clear();
    usePrimitiveStore.getState().set((state) => {
      state.resetSimulation = true;
      // This reset is from opening / newly saving a project, NOT a user Reset —
      // keep the loaded piston plate position (pistonSnapshot) so the plate reopens
      // where it was saved instead of snapping to the box top.
      state.resetKeepPiston = true;
      state.regressionAnalysis = false;
      // Stop any in-flight simulation so the play/pause button on the
      // new project reads "play", not "pause". Without this, opening a
      // project mid-run leaves startSimulation = true even though the
      // engine has been reset.
      state.startSimulation = false;
    });
  };

  // Fetch a project from Firestore and apply it, with stale-result protection.
  // If the user clicks A then B before A's getDoc resolves, A's apply path is
  // dropped instead of overwriting B's state.
  const loadProject = async (uid: string, title: string) => {
    const myToken = ++loadProjectTokenRef.current;
    setWaiting(true);
    try {
      await fetchProject(uid, title, (ps) => {
        if (myToken !== loadProjectTokenRef.current) return;
        setProjectState(ps);
      });
    } finally {
      if (usePrimitiveStore.getState().deepLinkPending) {
        usePrimitiveStore.getState().set((state) => {
          state.deepLinkPending = false;
        });
      }
      // Only the latest call should clear the spinner / run postFetch
      if (myToken === loadProjectTokenRef.current) {
        setWaiting(false);
        postFetch();
        // Record this open in Recent Projects. Read the just-applied type
        // from the store; stamp the access time as "now" for ordering/display.
        const applied = useStore.getState().projectState;
        if (applied.title === title) {
          recordRecentProject(uid, title, applied.type, new Date().getTime());
          // When a user opens THEIR OWN project, strip the ?userid=&project=
          // deep-link params from the URL. Those params only bootstrap the
          // very first open; leaving them makes the address bar disagree with
          // whatever project the user switches to afterward (open via deep
          // link, then pick a different project in the gallery → the URL still
          // names the first one). A shared-link view of someone else's project
          // (owner ≠ me) keeps its params — there the URL *is* the share.
          const me = useStore.getState().user.uid;
          if (me && uid === me && window.location.search) {
            window.history.replaceState(null, '', window.location.pathname + window.location.hash);
          }
        }
      }
    }
  };

  const setProjectState = (projectState: ProjectState | null) => {
    if (!projectState) return;
    setCommonStore((state) => {
      state.projectState = { ...projectState };
    });
    // Another project's undo entries must not act on this one.
    clearUndoHistory();
    usePrimitiveStore.getState().set((state) => {
      state.updateProjectsFlag = true;
      state.changed = false;
      // Force the dynamics viewer to rebuild its render molecules + mirror
      // engine from the freshly-loaded atoms even when RELOADING THE SAME
      // project — its rebuild effect otherwise keys only on the structural
      // signature (names + SDF), which is unchanged on a same-project reload,
      // leaving the viewer/mirror on the pre-reload state (stale minimizer,
      // stale readouts). See PrimitiveStoreState.sceneReloadFlag.
      state.sceneReloadFlag = (state.sceneReloadFlag ?? 0) + 1;
    });
    resetProject();
  };

  // List the user's projects from the compact `projectIndex` map on the user
  // doc — a single small read, instead of scanning the whole projects
  // subcollection (which downloaded every multi-MB project document just to
  // show title/type/timestamp). The index is written atomically alongside each
  // save/delete/rename/publish, so it stays in step with the subcollection.
  // Accounts that predate the index (readProjectIndex returns null) fall back
  // to a one-time reconcile, which scans the subcollection once and writes the
  // map so subsequent listings are cheap. `force` runs that reconcile
  // unconditionally — the manual repair path if the index ever drifts.
  const fetchMyProjects = async (silent: boolean, force = false) => {
    const uid = user.uid;
    if (!uid) return;
    if (!silent) setWaiting(true);
    try {
      const list = force
        ? await reconcileProjectIndex(uid)
        : (await readProjectIndex(uid)) ?? (await reconcileProjectIndex(uid));
      myProjectsRef.current = list;
      setUpdateMyProjectsFlag((prev) => !prev);
    } catch (error) {
      myProjectsRef.current = [];
      setMessage('error', t('message.CannotOpenYourProjects', lang) + ': ' + (error as Error).message);
    } finally {
      if (!silent) setWaiting(false);
    }
  };

  const listMyProjects = () => {
    if (user.uid) {
      fetchMyProjects(false).then(() => {
        usePrimitiveStore.getState().set((state) => {
          state.showProjectListPanel = true;
        });
        setUpdateMyProjectsFlag(!updateMyProjectsFlag);
      });
    }
  };

  // Soft delete: flag the project doc { deleted, deletedAt, purgeAt }, move its
  // index slot projectIndex → trashIndex, and unpublish it. The doc and all its
  // Storage blobs stay put, so Restore is lossless. `purgeAt` is a Firestore
  // Timestamp TRASH_RETENTION_DAYS in the future — a native Firestore TTL policy
  // on the `projects` collection group auto-deletes the doc once it passes (no
  // Cloud Function needed). TTL removes only the document; the now-stale
  // trashIndex slot is pruned client-side by fetchMyTrash.
  const deleteProject = async (title: string) => {
    const uid = user.uid;
    if (!uid) return;
    try {
      const deletedAt = Date.now();
      const purgeAt = Timestamp.fromMillis(deletedAt + TRASH_RETENTION_DAYS * 86400000);
      const projectRef = doc(firestore, 'users', uid, 'projects', title);
      // Pull the index entry (type/timestamp) before we move it, so the trash
      // slot keeps the original metadata for the list and a later restore.
      const existing = myProjectsRef.current?.find((p) => p.title === title);
      const info: ProjectInfo = existing ?? {
        title,
        timestamp: deletedAt,
        type: useStore.getState().projectState.type,
      };

      // Flag the project doc AND move its index slot projectIndex → trashIndex
      // in one atomic batch, so a mid-way failure can't leave the doc trashed
      // but still in the live list (or vice versa).
      const batch = writeBatch(firestore);
      batch.update(projectRef, { deleted: true, deletedAt, purgeAt });
      batch.set(
        doc(firestore, 'users', uid),
        {
          projectIndex: { [title]: deleteField() },
          trashIndex: { [title]: { timestamp: info.timestamp, type: info.type, deletedAt } },
        },
        { merge: true },
      );
      await batch.commit();

      // A trashed project must not stay in the Commons. Best-effort (kept out of
      // the batch so a failed/again-absent public delete can't abort the trash): the
      // owner-uid + title pair is stable, so deleteDoc on a missing doc is a
      // no-op whether or not it was actually published.
      try {
        await deleteDoc(doc(firestore, 'publicProjects', publicProjectDocId(uid, title)));
      } catch (e) {
        console.error('failed to remove publicProjects entry on delete', e);
      }

      // Drop it from the live "My Projects" list (it now lives in Trash). The
      // Trash tab re-reads trashIndex when opened, so no need to refresh it here.
      if (myProjectsRef.current) {
        const index = myProjectsRef.current.findIndex((p) => p.title === title);
        if (index !== -1) myProjectsRef.current.splice(index, 1);
        setUpdateFlag((prev) => !prev);
        if (showProjectListPanel) {
          setUpdateMyProjectsFlag((prev) => !prev);
          setUpdateFlag((prev) => !prev);
        }
      }

      setCommonStore((state) => {
        if (title === state.projectState.title) {
          state.projectState = ProjectUtil.createDefaultProjectState();
          clearUndoHistory();
        }
        // Drop the deleted project from Recent Projects so the menu can't
        // offer to re-open something that's now in the Trash.
        state.recentlyOpenedProjects = state.recentlyOpenedProjects.filter(
          (p) => !(p.owner === uid && p.title === title),
        );
      });
    } catch (error) {
      setMessage('error', t('message.CannotDeleteProject', lang) + ': ' + (error as Error).message);
    }
  };

  // Delete a project's Storage blobs (Commons thumbnail + per-structure bio
  // thumbnails). The only app-owned Storage for a project; molecule data is
  // inline in the Firestore doc. Best-effort — a missing object is harmless.
  const purgeProjectBlobs = async (uid: string, title: string) => {
    await deleteThumbnail(uid, title);
    await reconcileBioThumbnails(uid, title, []);
  };

  // Read the Trash list off the user doc into myTrashRef and return it (the
  // ref alone can't trigger a re-render, so the panel keeps the returned list
  // in local state). Native TTL deletes the project DOC after retention but
  // leaves the trashIndex slot behind, so we prune any expired slots here:
  // entries past the retention window are dropped from the map (a single merge
  // write) and excluded from the returned list, keeping Trash honest without a
  // Cloud Function.
  const fetchMyTrash = async (): Promise<ProjectInfo[]> => {
    const uid = user.uid;
    if (!uid) return [];
    try {
      const all = (await readTrashIndex(uid)) ?? [];
      const cutoffMs = TRASH_RETENTION_DAYS * 86400000;
      const expired = (p: ProjectInfo) => (p.deletedAt ?? 0) + cutoffMs <= Date.now();
      const live = all.filter((p) => !expired(p));
      const stale = all.filter(expired);
      if (stale.length > 0) {
        const payload: Record<string, ReturnType<typeof deleteField>> = {};
        for (const p of stale) payload[p.title] = deleteField();
        await setDoc(doc(firestore, 'users', uid), { trashIndex: payload }, { merge: true }).catch((e) =>
          console.error('Failed to prune expired trash entries:', e),
        );
        // Native TTL deletes the project DOC but not its Storage blobs, so when
        // we notice an expired (purged) entry we reclaim its thumbnails here —
        // otherwise they'd leak forever. Best-effort.
        await Promise.all(stale.map((p) => purgeProjectBlobs(uid, p.title)));
      }
      myTrashRef.current = live;
    } catch (error) {
      myTrashRef.current = [];
      console.error('Failed to read trash index:', error);
    }
    return myTrashRef.current;
  };

  // Titles currently in Trash. A project's Firestore doc id IS its title, so a
  // trashed project still occupies its title slot — creating / renaming /
  // saving-as to a trashed title would silently overwrite that doc. The create
  // paths use this to reject the reuse.
  const readTrashedTitles = async (uid: string): Promise<Set<string>> => {
    try {
      return new Set(((await readTrashIndex(uid)) ?? []).map((p) => p.title));
    } catch (e) {
      console.error('Failed to read trashed titles:', e);
      return new Set();
    }
  };

  // Restore a trashed project: clear its { deleted, deletedAt, purgeAt } flags
  // (dropping purgeAt exempts it from TTL) and move its slot trashIndex →
  // projectIndex. It comes back private (trashing unpublished it); the user can
  // re-share from the list. If TTL already purged the doc, there's nothing to
  // restore — drop the stale slot and tell the user.
  const restoreProject = async (title: string) => {
    const uid = user.uid;
    if (!uid) return;
    try {
      const entry = myTrashRef.current.find((p) => p.title === title);
      const info: ProjectInfo = entry
        ? { title, timestamp: entry.timestamp, type: entry.type }
        : { title, timestamp: Date.now(), type: useStore.getState().projectState.type };
      const projectRef = doc(firestore, 'users', uid, 'projects', title);
      // A batch.update would throw not-found if the doc is gone (TTL-purged or
      // never saved) — check first so we can show a clean "already purged" note.
      if (!(await getDoc(projectRef)).exists()) {
        await removeTrashIndexEntry(uid, title);
        await purgeProjectBlobs(uid, title); // doc gone via TTL — reclaim its blobs
        const j = myTrashRef.current.findIndex((p) => p.title === title);
        if (j !== -1) myTrashRef.current.splice(j, 1);
        setUpdateMyProjectsFlag((prev) => !prev);
        setMessage('info', t('projectListPanel.ProjectAlreadyPurged', lang));
        return;
      }
      // Clear the trash flags AND move the slot trashIndex → projectIndex in one
      // atomic batch, so the doc can't end up live-but-still-in-trash.
      const batch = writeBatch(firestore);
      batch.update(projectRef, {
        deleted: deleteField(),
        deletedAt: deleteField(),
        purgeAt: deleteField(),
      });
      batch.set(
        doc(firestore, 'users', uid),
        {
          trashIndex: { [title]: deleteField() },
          projectIndex: { [title]: { timestamp: info.timestamp, type: info.type } },
        },
        { merge: true },
      );
      await batch.commit();
      const i = myTrashRef.current.findIndex((p) => p.title === title);
      if (i !== -1) myTrashRef.current.splice(i, 1);
      await fetchMyProjects(true);
      setUpdateMyProjectsFlag((prev) => !prev);
      setUpdateFlag((prev) => !prev);
      setMessage('success', t('projectListPanel.ProjectRestored', lang) + '.');
    } catch (error) {
      setMessage('error', t('message.CannotOpenYourProjects', lang) + ': ' + (error as Error).message);
    }
  };

  // Permanent delete from the Trash view: hard-delete the doc, its public
  // index entry, its trash slot, and its Storage blobs (commons thumbnail +
  // per-structure bio thumbnails). Irreversible.
  const deleteProjectForever = async (title: string) => {
    const uid = user.uid;
    if (!uid) return;
    try {
      await deleteDoc(doc(firestore, 'users', uid, 'projects', title));
      try {
        await deleteDoc(doc(firestore, 'publicProjects', publicProjectDocId(uid, title)));
      } catch (e) {
        console.error('failed to remove publicProjects entry on hard delete', e);
      }
      await removeTrashIndexEntry(uid, title);
      await purgeProjectBlobs(uid, title); // best-effort Storage cleanup
      const i = myTrashRef.current.findIndex((p) => p.title === title);
      if (i !== -1) myTrashRef.current.splice(i, 1);
      setUpdateMyProjectsFlag((prev) => !prev);
      setUpdateFlag((prev) => !prev);
    } catch (error) {
      setMessage('error', t('message.CannotDeleteProject', lang) + ': ' + (error as Error).message);
    }
  };

  // Permanently delete every trashed project. Hard-deletes each doc, its public
  // entry, and its Storage blobs, then wipes the whole trashIndex map in one
  // write. Irreversible. Reads the authoritative trashIndex so it also clears
  // entries this session never loaded into myTrashRef.
  const emptyTrash = async () => {
    const uid = user.uid;
    if (!uid) return;
    try {
      const titles = ((await readTrashIndex(uid)) ?? []).map((p) => p.title);
      if (titles.length === 0) {
        myTrashRef.current = [];
        return;
      }
      for (const title of titles) {
        await deleteDoc(doc(firestore, 'users', uid, 'projects', title));
        try {
          await deleteDoc(doc(firestore, 'publicProjects', publicProjectDocId(uid, title)));
        } catch (e) {
          console.error('failed to remove publicProjects entry on empty trash', e);
        }
        await purgeProjectBlobs(uid, title);
      }
      // Drop the whole map at once rather than slot-by-slot.
      await setDoc(doc(firestore, 'users', uid), { trashIndex: deleteField() }, { merge: true });
      myTrashRef.current = [];
      setUpdateMyProjectsFlag((prev) => !prev);
      setUpdateFlag((prev) => !prev);
    } catch (error) {
      setMessage('error', t('message.CannotDeleteProject', lang) + ': ' + (error as Error).message);
    }
  };

  const renameProjectAndUpdateList = (uid: string, oldTitle: string, newTitle: string) => {
    if (!myProjectsRef.current) return;
    let index = -1;
    let oldProject = null;
    let newProject = null;
    for (const [i, p] of myProjectsRef.current.entries()) {
      if (p.title === oldTitle) {
        index = i;
        // Carry isPublic across the rename so the panel keeps showing the
        // right Publish/Unpublish item without a second fetch.
        oldProject = { title: oldTitle, timestamp: p.timestamp, type: p.type, isPublic: p.isPublic } as ProjectInfo;
        newProject = { title: newTitle, timestamp: p.timestamp, type: p.type, isPublic: p.isPublic } as ProjectInfo;
        break;
      }
    }
    if (index !== -1 && newProject && oldProject) {
      myProjectsRef.current.splice(index, 1);
      myProjectsRef.current.push(newProject);
      removeProjectIndexEntry(uid, oldTitle).then(() => {
        // ignore for now
      });
      setProjectIndexEntry(uid, newProject).then(() => {
        // ignore for now
      });
    }
  };

  const renameProject = async (oldTitle: string, newTitle: string) => {
    const uid = user?.uid;
    if (!uid) return;

    await fetchMyProjects(false);

    const projectExists = myProjectsRef.current?.some((p) => p.title === newTitle);
    if (projectExists) {
      setMessage('info', t('message.TitleUsedChooseDifferentOne', lang) + ': ' + newTitle);
      return;
    }
    // A trashed project still owns its title (doc id = title) — renaming onto it
    // would overwrite the trashed doc, so reject it too.
    if ((await readTrashedTitles(uid)).has(newTitle)) {
      setMessage('info', t('message.TitleUsedInTrash', lang) + ': ' + newTitle);
      return;
    }

    try {
      const oldRef = doc(firestore, 'users', uid, 'projects', oldTitle);
      const newRef = doc(firestore, 'users', uid, 'projects', newTitle);

      const oldSnap = await getDoc(oldRef);

      if (oldSnap.exists()) {
        const oldData = oldSnap.data();
        const newData = JSON.parse(JSON.stringify(oldData));
        newData.title = newTitle;
        // The compressed chat + biomolecule sidecars are Firestore Bytes values;
        // JSON.stringify mangles them, so copy the original references onto the
        // renamed doc.
        if (oldData[CHAT_GZ_FIELD]) newData[CHAT_GZ_FIELD] = oldData[CHAT_GZ_FIELD];
        if (oldData[BIO_GZ_FIELD]) newData[BIO_GZ_FIELD] = oldData[BIO_GZ_FIELD];

        await setDoc(newRef, newData);
        await deleteDoc(oldRef);

        // If the project was published, the public index is keyed on the
        // old title — migrate it. Best-effort: rename succeeds regardless.
        if (oldData.isPublic) {
          try {
            const oldPublicRef = doc(firestore, 'publicProjects', publicProjectDocId(uid, oldTitle));
            const oldPublicSnap = await getDoc(oldPublicRef);
            const oldPublic = oldPublicSnap.exists() ? oldPublicSnap.data() : {};
            const newPublicRef = doc(firestore, 'publicProjects', publicProjectDocId(uid, newTitle));
            const newPublic: Record<string, any> = {
              ownerUid: uid,
              ownerName: user.displayName ?? null,
              title: newTitle,
              type: oldData.type,
              timestamp: oldData.timestamp,
              // Keep the original publish time + accumulated view count rather
              // than resetting them on a rename.
              publishedAt: oldPublic.publishedAt ?? Date.now(),
              viewCount: oldPublic.viewCount ?? 0,
              description: oldData.description ?? null,
            };
            // Carry over the thumbnail the commons card renders (thumbnailUrl,
            // or the legacy inline base64) — otherwise the renamed entry shows
            // "No preview". The Storage object stays at the old-title path, but
            // its download URL is permanent so the <img> keeps loading; the next
            // save rewrites it to the new-title path.
            if (oldPublic.thumbnailUrl) newPublic.thumbnailUrl = oldPublic.thumbnailUrl;
            if (oldPublic.thumbnail) newPublic.thumbnail = oldPublic.thumbnail;
            await setDoc(newPublicRef, newPublic);
            await deleteDoc(oldPublicRef);
          } catch (e) {
            console.error('failed to migrate publicProjects entry on rename', e);
          }
        }

        renameProjectAndUpdateList(uid, oldTitle, newTitle);
        setUpdateFlag((prev) => !prev);

        setCommonStore((state) => {
          if (state.projectState.title === oldTitle) {
            state.projectState.title = newTitle;
          }
          // Keep Recent Projects in sync with the new title.
          state.recentlyOpenedProjects = state.recentlyOpenedProjects.map((p) =>
            p.owner === uid && p.title === oldTitle ? { ...p, title: newTitle } : p,
          );
        });
      }
    } catch (error) {
      setMessage('error', t('message.CannotRenameProject', lang) + ': ' + (error as Error).message);
    }
  };

  // Toggle a project's published state. Updates Firestore, the user's
  // projectList entry, and the active projectState if it matches.
  const setPublishedState = async (title: string, makePublic: boolean): Promise<boolean> => {
    const uid = user.uid;
    if (!uid) return false;
    setWaiting(true);
    try {
      const entry = myProjectsRef.current?.find((p) => p.title === title);
      let type = entry?.type;
      let timestamp = entry?.timestamp;
      let description: string | null | undefined = undefined;
      // Pull description (and fill in type/timestamp if myProjectsRef is
      // missing them) from the source doc so the index row is informative.
      try {
        const snap = await getDoc(doc(firestore, 'users', uid, 'projects', title));
        if (snap.exists()) {
          const data = snap.data();
          type = type ?? data.type;
          timestamp = timestamp ?? data.timestamp;
          description = data.description ?? null;
        }
      } catch (e) {
        console.error('failed to read project for publish', e);
      }
      if (type === undefined || timestamp === undefined) {
        setMessage('error', t('message.CannotSaveProject', lang));
        return false;
      }
      // Capture a chamber thumbnail when sharing the currently-open project.
      // Skipped when sharing some other project from the My Projects panel,
      // because the chamber would be rendering a different scene.
      let thumbnail: string | null = null;
      if (makePublic && useStore.getState().projectState.title === title) {
        thumbnail = await captureThumbnail('lab');
      }
      const ok = makePublic
        ? await publishProjectRemote(uid, title, {
            type,
            timestamp,
            description,
            ownerName: user.displayName ?? null,
            thumbnail,
          })
        : await unpublishProjectRemote(uid, title);
      if (!ok) return false;

      if (entry) {
        entry.isPublic = makePublic;
        await setProjectIndexEntry(uid, entry);
      }
      setCommonStore((state) => {
        if (state.projectState.title === title) {
          state.projectState.isPublic = makePublic;
        }
      });
      setUpdateMyProjectsFlag((prev) => !prev);
      setUpdateFlag((prev) => !prev);
      return true;
    } finally {
      setWaiting(false);
    }
  };

  const publishProject = async (title: string) => setPublishedState(title, true);
  const unpublishProject = async (title: string) => setPublishedState(title, false);

  const createNewProject = async () => {
    if (!user || !user.uid) return;
    const title = usePrimitiveStore.getState().projectTitle;
    if (!title) {
      setMessage('error', t('message.CannotCreateNewProjectWithoutTitle', lang) + '.');
      return;
    }
    const newTitle = title.trim();
    if (newTitle.length === 0) {
      setMessage('error', t('message.CannotCreateNewProjectWithoutTitle', lang) + '.');
      return;
    }
    await fetchMyProjects(false);
    // The index can miss a project written outside the app (a seeded example), so
    // also check the doc itself — batch.set below would overwrite it silently.
    const exists =
      myProjectsRef.current?.some((p) => p.title === newTitle) ||
      (await getDoc(doc(firestore, 'users', user.uid, 'projects', newTitle))).exists();
    if (exists) {
      setMessage('info', t('message.TitleUsedChooseDifferentOne', lang) + ': ' + newTitle);
      return;
    }
    // A trashed project still owns its title (doc id = title) — reusing it would
    // overwrite the trashed doc, so reject it too.
    if ((await readTrashedTitles(user.uid)).has(newTitle)) {
      setMessage('info', t('message.TitleUsedInTrash', lang) + ': ' + newTitle);
      return;
    }

    const uid = user.uid;
    const timestamp = Date.now();

    // Pass projectType through to createDefaultProjectState so type-specific
    // defaults (e.g. activeAnalysisTab=='polymer' for Polymer Builder) land in
    // the initial state rather than getting clobbered by 'energy' first.
    const newType = usePrimitiveStore.getState().projectType ?? ProjectType.DRUG_DISCOVERY;
    const ps = ProjectUtil.createDefaultProjectState(newType);
    ps.timestamp = timestamp;
    ps.key = timestamp.toString();
    ps.time = dayjs(timestamp).format('MM/DD/YYYY hh:mm A');
    ps.title = newTitle;
    ps.owner = uid;
    ps.type = newType;
    ps.description = usePrimitiveStore.getState().projectDescription ?? null;
    ps.cameraPosition = DEFAULT_CAMERA_POSITION;
    ps.cameraRotation = DEFAULT_CAMERA_ROTATION;
    ps.cameraUp = DEFAULT_CAMERA_UP;
    ps.panCenter = DEFAULT_PAN_CENTER;

    try {
      const pi: ProjectInfo = {
        timestamp: ps.timestamp,
        title: ps.title,
        type: ps.type,
      };

      // Write the project doc and its index entry atomically so the My
      // Projects list can never reference a project that failed to save.
      const batch = writeBatch(firestore);
      batch.set(doc(firestore, 'users', uid, 'projects', newTitle), ps);
      batch.set(doc(firestore, 'users', uid), projectIndexSetPayload(pi), { merge: true });
      await batch.commit();

      // A freshly created project is now the active one — surface it in Recent
      // Projects too, matching saveProject / saveProjectAs.
      recordRecentProject(uid, newTitle, ps.type, ps.timestamp);

      myProjectsRef.current.push(pi);
      if (showProjectListPanel) {
        setUpdateMyProjectsFlag((prev) => !prev);
        setUpdateFlag((prev) => !prev);
      }

      // An AI turn still running belongs to the project being left — cancel
      // it, as opening a saved project does (fetchProject), or its result is
      // added to this new one.
      cancelCurrentGeneration();
      setCommonStore((state) => {
        state.projectState = ps;
      });
      clearUndoHistory();
      setUpdateMyProjectsFlag((prev) => !prev);
    } catch (error) {
      setMessage('error', t('message.CannotCreateNewProject', lang) + ': ' + (error as Error).message);
    } finally {
      setWaiting(false);
      resetProject();
    }
  };

  // Prepare a deep-cloned ProjectState for Firestore: mirror live atom state
  // from moleculesRef into both the to-be-saved copy and the live store, then
  // strip transient/derivable fields. No-op for non-molecular-modeling saves.
  const prepareProjectForSave = (ps: ProjectState) => {
    if (
      (ps.type === ProjectType.MOLECULAR_MODELING || ps.type === ProjectType.POLYMER_BUILDER) &&
      moleculesRef?.current
    ) {
      const source = moleculesRef.current;
      syncMoleculesFromRef(ps.testMolecules, source, true);
      setCommonStore((state) => {
        syncMoleculesFromRef(state.projectState.testMolecules, source, false);
      });
    }
    // Crystal Builder / Semiconductor Design: capture the live MD engine's
    // last coordinates + velocities so a paused crystal simulation reopens
    // where it was paused. crystalDynamicsRef is non-null only when a crystal
    // MD engine is mounted (null for molecular / polymer / drug projects and
    // view-only device stacks), so this needs no project-type guard. We only
    // touch the field when an engine is live: with the engine unmounted (e.g.
    // saving from the gallery), the snapshot already on `ps` is the last-known
    // good state and is preserved. captureCrystalDynamicsSnapshot returns null
    // before the first step (and after a Reset), in which case we drop the
    // field so the project reopens on the built lattice.
    const crystalDyn = useRefStore.getState().crystalDynamicsRef?.current;
    if (crystalDyn) {
      // Stamped with the crystal + supercell it was taken from, so a later load
      // of a different crystal with the same atom count can't inherit it.
      const snapshot = captureCrystalDynamicsSnapshot(
        crystalDyn,
        crystalDynamicsSnapshotSource(ps.chamberCrystal, ps.chamberSupercell),
      );
      if (snapshot) ps.chamberCrystalDynamics = snapshot;
      else delete ps.chamberCrystalDynamics;
    }
    // Molecular Modeling / Polymer: capture the live piston plate position +
    // velocity so a paused constant-pressure (boiling) run reopens with the plate
    // — and thus the gas volume — where it was. Only when the plate is live
    // (enabled + past its initial step); otherwise drop the field so the project
    // reopens flush with the box top.
    const mdMirror = useRefStore.getState().molecularDynamicsRef?.current;
    // A periodic cell the barostat has moved: the atoms synced above sit in the
    // LIVE cell, so that is the box the project saves (and the store keeps from
    // here on — the worker reads the change as a box edit and pins it as the
    // Reset target). A walled box is never moved by the engine.
    if (mdMirror && mdMirror.indexOfStep > 0 && (ps.molecularContainer.periodic ?? false)) {
      const c = mdMirror.container;
      const b = ps.molecularContainer;
      if (c.lx !== b.lx || c.ly !== b.ly || c.lz !== b.lz) {
        ps.molecularContainer = { ...b, lx: c.lx, ly: c.ly, lz: c.lz };
        setCommonStore((state) => {
          state.projectState.molecularContainer.lx = c.lx;
          state.projectState.molecularContainer.ly = c.ly;
          state.projectState.molecularContainer.lz = c.lz;
        });
      }
    }
    if (mdMirror) {
      if (!(ps.pistonEnabled ?? false)) {
        // Piston turned off → there is no plate to persist.
        delete ps.pistonSnapshot;
      } else if (mdMirror.indexOfStep > 0) {
        // A run has actually stepped this session — capture the live plate.
        // indexOfStep is a long-standing, reliably-synced field, unlike the
        // pistonInitialized packet flag.
        ps.pistonSnapshot = { position: mdMirror.pistonPosition, velocity: mdMirror.pistonVelocity };
      }
      // else: piston is enabled but the mirror has NOT stepped this session —
      // either a fresh reopen (mirror rebuilt at indexOfStep 0, its plate still
      // at the default 0), or the plate was placed via the Piston Position
      // control (which writes ps.pistonSnapshot directly without stepping). In
      // both cases KEEP whatever snapshot is already on ps. Overwriting it with
      // the un-stepped mirror's plate (0 = box top) is exactly what made a
      // save-without-run silently drop a restored or hand-placed plate, so the
      // project reopened flush with the top. Reset clears the snapshot
      // explicitly (see the reset effect in simulationControls), so keeping it
      // here can never preserve a stale post-reset plate.
      // The save also synced the live atoms into the store (above), so the plate
      // now matches the stored atoms: drop any pause-time reloadBaseline, in the
      // saved copy and in the store.
      if (ps.pistonSnapshot) {
        const clean = { position: ps.pistonSnapshot.position, velocity: ps.pistonSnapshot.velocity };
        ps.pistonSnapshot = clean;
        setCommonStore((state) => {
          state.projectState.pistonSnapshot = { ...clean };
        });
      }
    }
    // Persist the agent's between-turn delta baseline (the fingerprint stamped at
    // the end of its last reply) so the conversation and its baseline stay paired
    // across a reopen. Copy the LIVE primitive-store value as-is — never
    // re-fingerprint the current scene here, or on reload the delta would diff an
    // unchanged scene against itself and always come up empty.
    const agentSnapshot = usePrimitiveStore.getState().lastAgentSnapshot;
    if (agentSnapshot) ps.lastAgentSnapshot = agentSnapshot;
    else delete ps.lastAgentSnapshot;
    // Biomolecule Lab study-tool state lives in the EPHEMERAL primitive store;
    // copy the live values onto the saved scene so the project reopens with the
    // same tools engaged (inspect/measure picks + the partial-MD control panel,
    // restored running if it was running). Only for the biomolecule labs.
    if (isBiomoleculeProject(ps.type)) {
      const prim = usePrimitiveStore.getState();
      ps.bioInspectMode = prim.bioInspectMode;
      ps.bioPicks = prim.bioPicks;
      ps.bioPartialMD = prim.bioPartialMD;
      ps.bioSimAnalysisVisible = prim.bioSimAnalysisVisible;
      // Capture the live partial-MD trajectory (coords + velocities + step) so a
      // reopened project resumes the sim in place. The runner returns null before
      // the first step; when the runner is unmounted (saving from the gallery) the
      // snapshot already on `ps` is the last-known-good state and is preserved —
      // but if the sim was torn down (no config), drop any stale snapshot.
      const cap = useRefStore.getState().bioPartialMDRef?.current?.getSnapshot?.() ?? null;
      if (cap) ps.bioPartialMDSnapshot = cap;
      else if (!prim.bioPartialMD) delete ps.bioPartialMDSnapshot;
    }
    stripProjectStateForSave(ps);
  };

  // Attach the gzip-compressed chat log as the CHAT_GZ_FIELD Bytes value on the
  // doc about to be written, spending whatever byte budget the scene leaves
  // under Firestore's per-document limit. `chatMessages` must be captured from
  // the clone BEFORE prepareProjectForSave runs (it strips the plain array).
  // Surfaces a notice when the chat had to be trimmed to fit.
  const attachCompressedChat = (ps: ProjectState, chatMessages: ChatMessageInterface[] | undefined) => {
    const sceneBytes = new TextEncoder().encode(JSON.stringify(ps)).length;
    const budget = Math.max(20_000, SAFE_DOC_BYTES - sceneBytes);
    const { value, note } = buildChatField(chatMessages, budget);
    if (value) (ps as unknown as Record<string, unknown>)[CHAT_GZ_FIELD] = value;
    if (note) setMessage('info', note);
  };

  // Move the Biomolecular-Lab structures' coordinates into a gzip'd Bytes sidecar
  // so the doc stays under Firestore's 1 MiB limit (see biomoleculeCompression).
  // url-backed structures were already dropped by stripProjectStateForSave (they
  // re-fetch); this handles the no-url built/folded/mutated ones whose PDB text
  // is the only copy. Runs BEFORE attachCompressedChat so the chat budget
  // accounts for the sidecar. Reserves a 50 KB floor so the chat is never
  // starved when the structures are large.
  const attachCompressedBiomolecules = (ps: ProjectState) => {
    const set = ps.biomolecules ?? [];
    const candidates = set.filter((b) => !b.url && b.data).map((b) => ({ name: b.name, data: b.data as string }));
    // Strip inline coordinates from every no-url entry now — the kept ones live
    // in the sidecar, any dropped ones are intentionally discarded to fit.
    for (const b of set) if (!b.url) delete b.data;
    if (candidates.length === 0) return;
    const sceneBytes = new TextEncoder().encode(JSON.stringify(ps)).length;
    const budget = Math.max(20_000, SAFE_DOC_BYTES - sceneBytes - 50_000);
    const { value, note } = buildBiomoleculeField(candidates, budget);
    if (value) (ps as unknown as Record<string, unknown>)[BIO_GZ_FIELD] = value;
    if (note) setMessage('info', note);
  };

  const saveProject = async () => {
    // Live Firebase auth is the source of truth for the write — the persisted
    // `user.uid` in the store can outlive a revoked or expired session and
    // would otherwise reach Firestore as a stale uid and trip the rule.
    const liveUid = auth.currentUser?.uid;
    if (!user || !user.uid || !liveUid || liveUid !== user.uid) {
      setMessage('error', t('message.CannotSaveProject', lang) + ': ' + t('message.NotSignedIn', lang));
      return;
    }
    const uid = liveUid;
    const title = useStore.getState().projectState.title;
    if (!title) {
      setMessage('error', t('message.CannotSaveProjectWithoutTitle', lang));
      return;
    }
    setWaiting(true);
    // The preparation is inside the try: a throw here used to skip the finally,
    // leaving the progress spinner on forever with no message.
    let ps: ProjectState;
    try {
      ps = JSON.parse(JSON.stringify(useStore.getState().projectState)) as ProjectState;
      // Capture before prepareProjectForSave strips the plain array; re-attached
      // compressed below.
      const chatMessages = ps.chatMessages;
      ps.timestamp = new Date().getTime();
      ps.time = dayjs(new Date(ps.timestamp)).format('MM/DD/YYYY hh:mm A');
      ps.key = ps.timestamp.toString();
      // The project lives under users/{uid}/projects/{title} in Firestore, so
      // its `owner` field must match the writing user's uid. fetchProject sets
      // owner from the URL on load, which can be stale (e.g. you're saving a
      // copy of an example into your own collection). Force it to the current
      // user's uid so security rules that check `resource.data.owner ==
      // request.auth.uid` accept the write.
      ps.owner = uid;
      prepareProjectForSave(ps);
      attachCompressedBiomolecules(ps);
      attachCompressedChat(ps, chatMessages);
    } catch (error) {
      setWaiting(false);
      setMessage('error', t('message.CannotSaveProject', lang) + ': ' + (error as Error).message);
      return;
    }
    try {
      // Persist any freshly-captured biomolecule card thumbnails to Storage and
      // stamp their URLs onto ps.biomolecules before the doc is written (the
      // JPEGs are too big to inline in the 1 MiB Firestore doc). No-op for
      // non-bio projects and for structures not viewed this session.
      await uploadBioThumbnails(uid, title, ps.biomolecules ?? [], usePrimitiveStore.getState().bioThumbnails);
      // Only include isPublic when it's actually true — Firestore rejects
      // `undefined` values inside the written index map.
      const pi: ProjectInfo = { timestamp: ps.timestamp, title, type: ps.type };
      if (ps.isPublic === true) pi.isPublic = true;

      // Write the project doc and its index slot atomically. The index is keyed
      // by title, so this overwrites any prior entry — no separate removal.
      const batch = writeBatch(firestore);
      batch.set(doc(firestore, 'users', uid, 'projects', title), ps);
      batch.set(doc(firestore, 'users', uid), projectIndexSetPayload(pi), { merge: true });
      await batch.commit();

      // Now that the durable set is committed, prune Storage thumbnails for any
      // structure that was deleted (or renamed) since the last save. Tied to the
      // save (not the gallery delete) so cleanup follows the persisted state and
      // stays undo-safe. Fire-and-forget — orphan blobs are harmless if it fails.
      if (isBiomoleculeProject(ps.type)) {
        void reconcileBioThumbnails(
          uid,
          title,
          (ps.biomolecules ?? []).map((b) => b.name),
        );
      }

      if (myProjectsRef.current) {
        const existing = myProjectsRef.current.findIndex((p) => p.title === title);
        if (existing !== -1) myProjectsRef.current.splice(existing, 1);
        myProjectsRef.current.push(pi);
        setUpdateMyProjectsFlag((prev) => !prev);
      }
      // If this project is published, refresh its Commons entry so the public
      // card reflects this edit: bump the last-saved time (drives the "most
      // recent" sort) and re-sync the description + thumbnail. The saved project
      // is the one on screen, so a fresh lab capture is valid here. Fire-and-
      // forget — the save has already committed and this is a minor sync.
      if (ps.isPublic === true) {
        void (async () => {
          let thumbnail: string | null = null;
          try {
            thumbnail = await captureThumbnail('lab');
          } catch {
            // A capture failure just skips the thumbnail refresh; the rest of
            // the sync (timestamp + description) still goes through.
          }
          await syncPublicProjectMeta(uid, title, {
            timestamp: ps.timestamp,
            description: ps.description ?? null,
            ownerName: user.displayName ?? null,
            thumbnail,
          });
        })();
      }
      recordRecentProject(uid, title, ps.type, ps.timestamp);
    } catch (error) {
      setMessage('error', t('message.CannotSaveProject', lang) + ': ' + (error as Error).message);
    } finally {
      setWaiting(false);
      if (saveAndThenOpenProjectFlag) {
        // A recent-project open requested via the File menu carries its own
        // owner (it may belong to another user); prefer it over the legacy
        // projectToOpen path, which assumes the current user owns the target.
        const recent = useStore.getState().recentProjectToOpen;
        if (recent) {
          setCommonStore((state) => {
            state.recentProjectToOpen = null;
          });
          await loadProject(recent.owner, recent.title);
        } else {
          const projectToOpen = useStore.getState().projectToOpen?.title;
          if (projectToOpen) await loadProject(uid, projectToOpen);
        }
      }
      setChanged(false);
    }
  };

  const saveProjectAs = async () => {
    if (!user || !user.uid) return;
    const title = usePrimitiveStore.getState().projectTitle;
    if (!title) {
      setMessage('error', t('message.CannotCreateNewProjectWithoutTitle', lang) + '.');
      return;
    }
    const newTitle = title.trim();
    if (newTitle.length === 0) {
      setMessage('error', t('message.CannotCreateNewProjectWithoutTitle', lang) + '.');
      return;
    }
    setWaiting(true);
    try {
      await fetchMyProjects(false);

      // As in createNewProject: the index can miss a doc written outside the app.
      const exists =
        myProjectsRef.current?.some((p) => p.title === newTitle) ||
        (await getDoc(doc(firestore, 'users', user.uid, 'projects', newTitle))).exists();
      if (exists) {
        setMessage('info', t('message.TitleUsedChooseDifferentOne', lang) + ': ' + title);
        return;
      }
      // A trashed project still owns its title (doc id = title) — reusing it
      // would overwrite the trashed doc, so reject it too.
      if ((await readTrashedTitles(user.uid)).has(newTitle)) {
        setMessage('info', t('message.TitleUsedInTrash', lang) + ': ' + newTitle);
        return;
      }

      const uid = user.uid;
      const state = useStore.getState();
      const ps = JSON.parse(JSON.stringify(state.projectState)) as ProjectState;
      // Capture before prepareProjectForSave strips it; re-attached compressed below.
      const chatMessages = ps.chatMessages;

      const timestamp = Date.now();
      ps.timestamp = timestamp;
      ps.key = timestamp.toString();
      ps.time = dayjs(timestamp).format('MM/DD/YYYY hh:mm A');
      ps.title = newTitle;
      ps.owner = uid;
      // Keep the type from the live projectState (ps is a clone of it). The
      // primitive store's `projectType` only reflects the most recent New
      // Project dialog opening — if the user opened a Molecular Modeling
      // project's New Project dialog at some earlier point, reading from
      // here mis-stamps the polymer project as Molecular Modeling on save.
      ps.type = useStore.getState().projectState.type;
      ps.description = usePrimitiveStore.getState().projectDescription ?? '';

      prepareProjectForSave(ps);
      attachCompressedBiomolecules(ps);
      attachCompressedChat(ps, chatMessages);

      // Key the index entry by the actual doc id (newTitle), not the raw
      // dialog title — they can differ (e.g. trailing whitespace), and the
      // index key must match the project doc id the listing opens.
      const pi: ProjectInfo = {
        timestamp: ps.timestamp,
        title: newTitle,
        type: ps.type,
      };

      // Project doc + index slot written atomically.
      const batch = writeBatch(firestore);
      batch.set(doc(firestore, 'users', uid, 'projects', newTitle), ps);
      batch.set(doc(firestore, 'users', uid), projectIndexSetPayload(pi), { merge: true });
      await batch.commit();

      recordRecentProject(uid, newTitle, ps.type, ps.timestamp);

      if (myProjectsRef.current) {
        myProjectsRef.current.push(pi);

        if (showProjectListPanel) {
          setUpdateMyProjectsFlag((prev) => !prev);
          setUpdateFlag((prev) => !prev);
        }
        setCommonStore((state) => {
          state.projectState.owner = ps.owner;
          state.projectState.type = ps.type;
          state.projectState.title = ps.title;
          state.projectState.description = ps.description;
        });
      }
    } catch (error) {
      setMessage('error', t('message.CannotCreateNewProject', lang) + ': ' + (error as Error).message);
    } finally {
      setWaiting(false);
      setChanged(false);
    }
  };

  const showMyProjectsList = () => {
    usePrimitiveStore.getState().set((state) => {
      state.startSimulation = false;
    });
    listMyProjects();
  };

  const updateProjects = () => {
    setUpdateFlag(!updateFlag);
  };

  return {
    myProjectsRef,
    myTrashRef,
    setProjectState,
    loadProject,
    createNewProject,
    saveProject,
    saveProjectAs,
    showMyProjectsList,
    deleteProject,
    restoreProject,
    deleteProjectForever,
    emptyTrash,
    fetchMyTrash,
    renameProject,
    publishProject,
    unpublishProject,
    updateProjects,
  };
};
